import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  MenuItem,
  CircularProgress,
  Typography,
  Autocomplete,
  Paper,
  Divider,
  IconButton,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { resolveFieldsService } from '../../apis/EntitiesSelection';
import { fetchSqlTableFields, getConnectionById } from '../../apis/Connections';

const OPERATORS = [
  { value: 'less_than', label: 'Less Than' },
  { value: 'less_or_equal', label: 'Less Than or Equal' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'greater_or_equal', label: 'Greater Than or Equal' },
  { value: 'equal', label: 'Equal' },
  { value: 'not_equal', label: 'Not Equal' },
  { value: 'contains', label: 'Contains' },
  { value: 'starts_with', label: 'Starts With' },
  { value: 'ends_with', label: 'Ends With' },
];

const OPERATORS_BY_TYPE = {
  id: [
    'less_than',
    'less_or_equal',
    'greater_than',
    'greater_or_equal',
    'equal',
    'not_equal',
  ],
  string: ['contains', 'starts_with', 'ends_with', 'equal', 'not_equal'],
  textarea: ['contains', 'starts_with', 'ends_with', 'equal', 'not_equal'],
  address: ['contains', 'starts_with', 'ends_with', 'equal', 'not_equal'],
  url: ['contains', 'starts_with', 'ends_with', 'equal', 'not_equal'],
  picklist: ['contains', 'starts_with', 'ends_with', 'equal', 'not_equal'],
  reference: ['contains', 'starts_with', 'ends_with', 'equal', 'not_equal'],
  boolean: ['equal', 'not_equal'],
  date: [
    'less_than',
    'less_or_equal',
    'greater_than',
    'greater_or_equal',
    'equal',
    'not_equal',
  ],
  datetime: [
    'less_than',
    'less_or_equal',
    'greater_than',
    'greater_or_equal',
    'equal',
    'not_equal',
  ],
  int: [
    'less_than',
    'less_or_equal',
    'greater_than',
    'greater_or_equal',
    'equal',
    'not_equal',
  ],
  phone: [
    'less_than',
    'less_or_equal',
    'greater_than',
    'greater_or_equal',
    'equal',
    'not_equal',
  ],
  double: [
    'less_than',
    'less_or_equal',
    'greater_than',
    'greater_or_equal',
    'equal',
    'not_equal',
  ],
  currency: [
    'less_than',
    'less_or_equal',
    'greater_than',
    'greater_or_equal',
    'equal',
    'not_equal',
  ],
};

const DROPDOWN_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 320,
      overflowY: 'auto',
      borderRadius: 1,
      boxShadow: 3,
    },
  },
  MenuListProps: { dense: true },
  disableScrollLock: true,
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
};

// Helper functions for SQL source handling
const isSqlMappingConfig = (config = {}) => {
  const connector =
    config?.connection?.connector_name ||
    config?.connection?.connector ||
    config?.label ||
    config?.type ||
    '';
  return typeof connector === 'string'
    ? connector.toLowerCase().includes('sql')
    : false;
};

const buildSqlMetadataFromConnection = (connection = {}) => {
  if (!connection) return null;
  const connectorName =
    connection?.connector_name ||
    connection?.connector ||
    connection?.type ||
    '';
  if (
    typeof connectorName !== 'string' ||
    !connectorName.toLowerCase().includes('sql')
  )
    return null;
  const params = connection.custom_params || {};
  const token =
    params.token || params.quick_test_token || connection.token || null;
  return {
    token: token || null,
    host: params.host || '',
    port:
      params.port !== undefined && params.port !== null
        ? String(params.port)
        : '',
    database: params.database_name || params.db_name || params.database || '',
  };
};

const ensureSqlMetadataForConfig = async (config = {}) => {
  if (!config) return null;
  const direct = config.sqlMetadata || config.connection?.sqlMetadata || null;
  if (direct?.token) return direct;
  const inline = buildSqlMetadataFromConnection(config.connection || {});
  if (inline?.token) return inline;
  if (config.connection?.conn_id) {
    try {
      const connection = await getConnectionById(config.connection.conn_id);
      const hydrated = buildSqlMetadataFromConnection(connection);
      if (hydrated?.token) return hydrated;
      return hydrated || direct || null;
    } catch (err) {
      console.error('Failed to hydrate SQL metadata', err);
    }
  }
  return direct || inline || null;
};

const normalizeSqlFieldsResponse = (response) => {
  if (!response) return [];
  const candidates =
    response.fields?.fields ||
    response.fields ||
    response.columns ||
    response.schema ||
    response.data ||
    response.result ||
    response;
  const asArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === 'object') return Object.values(value);
    return [];
  };
  return asArray(candidates)
    .map((field) => {
      if (!field || typeof field !== 'object') return null;
      const fieldName =
        field.field_name ||
        field.column_name ||
        field.name ||
        field.columnName ||
        '';
      if (!fieldName) return null;
      const label =
        field.field_label ||
        field.display_name ||
        field.column_label ||
        field.column_name ||
        field.name ||
        fieldName;
      const nullableFlag =
        field.nullable === false || field.is_nullable === false
          ? true
          : undefined;
      const mandatory =
        field.is_mandatory ?? field.is_required ?? nullableFlag ?? false;
      return {
        field_name: fieldName,
        data_type: (
          field.data_type ||
          field.type ||
          field.column_type ||
          field.columnType ||
          'string'
        ).toLowerCase(),
        field_label: label,
        is_mandatory: Boolean(mandatory),
        is_creatable:
          field.is_creatable ?? field.isWritable ?? field.is_writable ?? true,
        is_updatable:
          field.is_updatable ?? field.isWritable ?? field.is_writable ?? true,
        is_queryable:
          field.is_queryable ?? field.isQueryable ?? field.is_queryable ?? true,
        is_primary_key:
          field.is_primary_key === true ||
          field.isPrimaryKey === true ||
          field.is_primary === true ||
          field.isPrimary === true ||
          false,
      };
    })
    .filter(Boolean);
};

const buildSqlResolvedEntries = async (configs = []) => {
  if (!Array.isArray(configs) || configs.length === 0) return [];
  const tasks = configs.map(async (config) => {
    const nodeId = config.id || config.nodeId;
    if (!nodeId) {
      console.warn(
        'SQL config missing id/nodeId:',
        config,
        'This may cause node ID inconsistencies',
      );
    }

    const metadata = await ensureSqlMetadataForConfig(config);
    if (!metadata?.token) {
      throw new Error(
        `SQL connector "${
          config?.connection?.conn_name || config?.label || nodeId
        }" is missing credentials. Please re-save the connection.`,
      );
    }

    const entities = Array.isArray(config.selectedEntities)
      ? config.selectedEntities
      : [];
    const perEntity = await Promise.all(
      entities.map(async (entity) => {
        const tableName =
          entity?.entity_name || entity?.entity || entity?.label || entity;
        if (!tableName) return null;
        const response = await fetchSqlTableFields(metadata.token, tableName);
        const fields = normalizeSqlFieldsResponse(response);
        return {
          node_id: nodeId,
          type: config.type || 'source',
          entity: tableName,
          entity_name: tableName,
          connection: config.connection,
          fields: {
            status: 'success',
            source: config.connection?.connector_name || 'SQL',
            module_name: 'SQL',
            entity_name: tableName,
            fields,
          },
        };
      }),
    );
    return perEntity.filter(Boolean);
  });
  const resolved = await Promise.all(tasks);
  return resolved.flat();
};

function useQueryableSourceFields(open) {
  const workflowsList = useSelector((state) => state.workflows?.list || []);
  const { workflowName: workflowParam } = useParams();
  const workflowId = useMemo(() => {
    const found = workflowsList.find(
      (w) =>
        String(w.workflow_id) === String(workflowParam) ||
        w.name === workflowParam,
    );
    return found?.workflow_id || workflowParam;
  }, [workflowParam, workflowsList]);
  const finalConfigPayload = useSelector(
    (state) => state.mapping.finalConfigPayloadByWorkflow?.[workflowId],
  );

  const sourceOnlyPayload = useMemo(
    () => (finalConfigPayload || []).filter((cfg) => cfg.type === 'source'),
    [finalConfigPayload],
  );

  const entityToConnection = useMemo(() => {
    const map = {};
    (sourceOnlyPayload || []).forEach((cfg) => {
      const conn = cfg?.config?.connection || cfg?.connection || {};
      const conn_name = conn?.conn_name || conn?.name || '';
      const conn_id = conn?.conn_id || conn?.id || '';
      const module = cfg?.config?.module || cfg?.module || '';
      const selectedEntities = cfg?.selectedEntities || [];
      selectedEntities.forEach((ent) => {
        const entity_name = ent?.entity_name || ent?.entity || '';
        if (entity_name) {
          map[entity_name] = { conn_name, conn_id, module };
        }
      });
    });
    return map;
  }, [sourceOnlyPayload]);

  const connectionToEntities = useMemo(() => {
    const map = {};
    (sourceOnlyPayload || []).forEach((cfg) => {
      const conn = cfg?.config?.connection || cfg?.connection || {};
      const name = conn?.conn_name || conn?.name || '';
      const selectedEntities = cfg?.selectedEntities || [];
      const entities = selectedEntities
        .map((ent) => ent?.entity_name || ent?.entity)
        .filter(Boolean);
      if (name) {
        map[name] = Array.from(new Set(entities));
      }
    });
    return map;
  }, [sourceOnlyPayload]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [options, setOptions] = useState([]);

  useEffect(() => {
    let mounted = true;
    const fetchFields = async () => {
      if (!open) return;
      if (!workflowId || !sourceOnlyPayload.length) return;
      setLoading(true);
      setError(null);
      try {
        // Separate SQL and non-SQL configs
        const sqlConfigs = [];
        const nonSqlConfigs = [];

        sourceOnlyPayload.forEach((cfg) => {
          if (isSqlMappingConfig(cfg)) {
            sqlConfigs.push(cfg);
          } else {
            nonSqlConfigs.push(cfg);
          }
        });

        let result = [];

        // Handle non-SQL sources (e.g., Salesforce)
        if (nonSqlConfigs.length > 0) {
          const resolved = await resolveFieldsService({
            label: 'FieldResolution',
            configs: nonSqlConfigs,
            force: true,
          });
          result = Array.isArray(resolved) ? resolved : [];
        }

        // Handle SQL sources
        if (sqlConfigs.length > 0) {
          const sqlResolved = await buildSqlResolvedEntries(sqlConfigs);
          result = [...result, ...sqlResolved];
        }

        // Normalize the combined results
        const normalized = (Array.isArray(result) ? result : []).flatMap(
          (ent) => {
            const entityName =
              ent?.entity || ent?.fields?.entity_name || ent?.entity_name || '';
            const fieldsArr = Array.isArray(ent?.fields?.fields)
              ? ent.fields.fields
              : Array.isArray(ent?.fields)
                ? ent.fields
                : [];

            return fieldsArr
              .filter((f) => f?.is_queryable === true)
              .map((f) => ({
                entity_name: entityName,
                field_name: f.field_name || f.field_label,
                field_label: f.field_label || f.field_name,
                data_type: f.data_type || 'string',
              }));
          },
        );
        if (mounted) setOptions(normalized);
      } catch (e) {
        console.error(
          'Failed to fetch source fields for Save & Close dialog:',
          e,
        );
        if (mounted) setError('Failed to load source fields');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchFields();
    return () => {
      mounted = false;
    };
  }, [open, workflowId, sourceOnlyPayload]);

  const connectionNames = useMemo(() => {
    const set = new Set();
    (sourceOnlyPayload || []).forEach((cfg) => {
      const conn = cfg?.config?.connection || cfg?.connection || {};
      const name = conn?.conn_name || conn?.name || '';
      if (name) set.add(name);
    });
    return Array.from(set);
  }, [sourceOnlyPayload]);

  return {
    loading,
    error,
    options,
    entityToConnection,
    connectionNames,
    connectionToEntities,
  };
}

const SaveCloseDialog = ({ open, onClose, onConfirm, onSkip }) => {
  const {
    loading,
    error,
    options,
    entityToConnection,
    connectionNames,
    connectionToEntities,
  } = useQueryableSourceFields(open);

  const { workflowName } = useParams();
  const storedFilterExpression = useSelector(
    (state) => state.mapping?.filterExpressionByWorkflow?.[workflowName],
  );

  const [conditions, setConditions] = useState([
    { fieldKey: '', operator: '', value: '', conn_name: null },
  ]);
  const [logicalOperator, setLogicalOperator] = useState('AND');
  const connectionList = connectionNames;

  useEffect(() => {
    if (!open) {
      setConditions([
        { fieldKey: '', operator: '', value: '', conn_name: null },
      ]);
      setLogicalOperator('AND');
    }
  }, [open]);

  // Default the single available connection for the first condition when only one exists
  useEffect(() => {
    if (!open) return;
    if (connectionList.length === 1) {
      setConditions((prev) =>
        prev.map((c, i) =>
          i === 0 ? { ...c, conn_name: connectionList[0] } : c,
        ),
      );
    }
  }, [open, connectionList]);

  useEffect(() => {
    if (!open) return;
    try {
      let initial = storedFilterExpression;
      if (!initial) {
        const raw = localStorage.getItem(`workflow_filters:${workflowName}`);
        if (raw) initial = JSON.parse(raw);
      }
      if (initial) {
        const group = initial?.filterGroup ? initial.filterGroup : initial;
        const normalized = Array.isArray(group?.conditions)
          ? group.conditions
          : [];

        setConditions(
          normalized.map((c) => ({
            fieldKey: `${c.entity_name || ''}::${c.source_field || ''}`,
            operator: c.operator || '',
            value: c.input_value ?? '',
            conn_name:
              c?.connection_scope?.conn_name ||
              (connectionList.length === 1 ? connectionList[0] : null),
          })),
        );
        setLogicalOperator(group?.condition_operator || group?.logic || 'AND');

        // Global connection selectors removed; per-condition connection is set above via `conn_name`.
      }
    } catch (e) {
      console.error(
        'Failed to parse filter expression for Save & Close dialog:',
        e,
      );
    }
  }, [open, storedFilterExpression, workflowName, connectionList]);

  const parseFieldKey = (key) => {
    const [entity_name = '', field_name = ''] = (key || '').split('::');
    return { entity_name, field_name };
  };

  const getSelectedFieldObj = (fieldKey) =>
    options.find((opt) => `${opt.entity_name}::${opt.field_name}` === fieldKey);

  const allowedOperatorsFor = (fieldKey) => {
    const obj = getSelectedFieldObj(fieldKey);
    const t = (obj?.data_type || '').toLowerCase();
    return OPERATORS_BY_TYPE[t] || OPERATORS.map((op) => op.value);
  };

  const filteredOperatorsFor = (fieldKey) => {
    const allowed = allowedOperatorsFor(fieldKey);
    return OPERATORS.filter((op) => allowed?.includes(op.value));
  };

  const updateCondition = (idx, patch) => {
    setConditions((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    );
  };

  const addCondition = () =>
    setConditions((prev) => [
      ...prev,
      {
        fieldKey: '',
        operator: '',
        value: '',
        conn_name: connectionList.length === 1 ? connectionList[0] : null,
      },
    ]);
  const removeCondition = (idx) =>
    setConditions((prev) => prev.filter((_, i) => i !== idx));

  const canSubmit = useMemo(() => {
    if (!conditions.length) return false;
    const hasAll = conditions.every(
      (c) => c.fieldKey && c.operator && c.value !== '',
    );
    return hasAll;
  }, [conditions]);

  const handleSubmit = () => {
    if (!canSubmit) return;

    const normalized = conditions.map((c) => {
      const selected = getSelectedFieldObj(c.fieldKey);
      const parsed = parseFieldKey(c.fieldKey);
      return {
        entity_name: parsed.entity_name || selected?.entity_name || '',
        source_field: parsed.field_name || selected?.field_name || '',
        operator: c.operator,
        input_value: c.value,
        _conn_name:
          c.conn_name ||
          (entityToConnection[parsed.entity_name || selected?.entity_name || '']
            ?.conn_name ??
            null),
      };
    });

    const connIdByName = (name) => {
      if (!name) return null;
      let id = null;
      Object.values(entityToConnection || {}).some((info) => {
        if (info?.conn_name === name) {
          id = info?.conn_id || null;
          return true;
        }
        return false;
      });
      return id;
    };

    const payload = {
      filterGroup: {
        ...(normalized.length > 1
          ? { condition_operator: logicalOperator || 'AND' }
          : {}),
        conditions: normalized.map((c) => {
          const targetConnName = c._conn_name || null;
          const targetConnId = connIdByName(targetConnName);
          return {
            ...c,
            connection_scope: targetConnName
              ? { conn_name: targetConnName, conn_id: targetConnId }
              : null,
          };
        }),
      },
    };
    try {
      localStorage.setItem(
        `workflow_filters:${workflowName}`,
        JSON.stringify(payload),
      );
    } catch (e) {
      console.log(e);
    }
    onConfirm?.(payload);
  };

  const handleSkip = () => {
    try {
      localStorage.removeItem(`workflow_filters:${workflowName}`);
    } catch (e) {
      console.log(e);
    }
    onSkip?.();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          py: 2,
          px: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <FilterIcon sx={{ color: '#5965A6' }} />
        <Typography variant="h6" component="div" fontWeight={600}>
          Save & Close
        </Typography>
        <Box sx={{ ml: 'auto' }}>
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          {loading && (
            <Alert
              severity="info"
              sx={{
                mb: 3,
                borderRadius: 2,
                '& .MuiAlert-icon': { alignItems: 'center' },
              }}
              icon={<CircularProgress size={20} />}
            >
              <Typography variant="body2">Loading source fields…</Typography>
            </Alert>
          )}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 2,
              }}
            >
              <Typography variant="body2">{error}</Typography>
            </Alert>
          )}

          <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <InfoIcon sx={{ color: '#5965A6' }} fontSize="small" />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Data extraction occurs only for records that match the filter
                conditions. When multiple source connections are selected
                (many-to-one), each condition is evaluated within its connection
                scope using the chosen logical operator.
              </Typography>
            </Box>
          </Paper>

          {/* Conditions list */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: '#1e293b',
                  fontSize: '1.1rem',
                }}
              >
                Filter Conditions
              </Typography>
              <ToggleButtonGroup
                value={logicalOperator}
                exclusive
                size="small"
                onChange={(_, val) => {
                  if (val) setLogicalOperator(val);
                }}
                aria-label="Logical Operator"
              >
                <ToggleButton
                  value="AND"
                  sx={{
                    '&.Mui-selected': {
                      bgcolor: '#5965A6',
                      color: '#ffffff',
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  AND
                </ToggleButton>
                <ToggleButton
                  value="OR"
                  sx={{
                    '&.Mui-selected': {
                      bgcolor: '#5965A6',
                      color: '#ffffff',
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  OR
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {/* Global connection selectors removed. Each condition now has its own connection picker. */}
            {conditions.map((cond, idx) => (
              <Paper
                key={`cond-${idx}`}
                elevation={0}
                sx={{
                  p: 2,
                  mb: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                }}
              >
                {/* Header row: show connection chosen for this condition */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  {(() => {
                    let label = 'Filter Condition';
                    if (cond.conn_name) {
                      label = `Connection: ${cond.conn_name}`;
                    } else {
                      const { entity_name } = parseFieldKey(cond.fieldKey);
                      const conn = entityToConnection[entity_name] || {};
                      if (conn.conn_name)
                        label = `Connection: ${conn.conn_name}`;
                    }
                    return (
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', fontWeight: 600, mb: 1 }}
                      >
                        {label}
                      </Typography>
                    );
                  })()}
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(4, 1fr) auto',
                    },
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  <Autocomplete
                    size="small"
                    options={connectionList}
                    value={cond.conn_name || null}
                    isOptionEqualToValue={(opt, val) => opt === val}
                    onChange={(_, val) => {
                      const newConn = val || null;
                      const allowed = new Set(
                        connectionToEntities[newConn] || [],
                      );
                      const { entity_name } = parseFieldKey(cond.fieldKey);
                      const keepField = entity_name && allowed.has(entity_name);
                      updateCondition(idx, {
                        conn_name: newConn,
                        ...(keepField
                          ? {}
                          : { fieldKey: '', operator: '', value: '' }),
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Choose Connection"
                        size="small"
                      />
                    )}
                  />
                  <Autocomplete
                    size="small"
                    options={(() => {
                      const allowedEntities = (() => {
                        if (connectionList.length > 1) {
                          if (cond.conn_name) {
                            return new Set(
                              connectionToEntities[cond.conn_name] || [],
                            );
                          }
                          return new Set();
                        }
                        // Single-connection case: show that connection's entities
                        const onlyConn = connectionList[0];
                        return new Set(connectionToEntities[onlyConn] || []);
                      })();
                      const filtered = options.filter((opt) => {
                        // If no entities allowed, show none
                        if (allowedEntities.size === 0) return false;
                        return allowedEntities.has(opt.entity_name);
                      });
                      return filtered.map((opt) => ({
                        label: opt.field_label,
                        value: `${opt.entity_name}::${opt.field_name}`,
                        entity_name: opt.entity_name,
                        field_name: opt.field_name,
                        data_type: opt.data_type,
                      }));
                    })()}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    groupBy={(option) => option.entity_name || ''}
                    getOptionLabel={(option) => option.label || ''}
                    value={(() => {
                      const allowedEntities = (() => {
                        if (connectionList.length > 1) {
                          if (cond.conn_name) {
                            return new Set(
                              connectionToEntities[cond.conn_name] || [],
                            );
                          }
                          return new Set();
                        }
                        const onlyConn = connectionList[0];
                        return new Set(connectionToEntities[onlyConn] || []);
                      })();
                      const opt = options
                        .filter((o) => {
                          if (allowedEntities.size === 0) return false;
                          return allowedEntities.has(o.entity_name);
                        })
                        .find(
                          (o) =>
                            `${o.entity_name}::${o.field_name}` ===
                            cond.fieldKey,
                        );
                      return opt
                        ? {
                            label: opt.field_label,
                            value: `${opt.entity_name}::${opt.field_name}`,
                            entity_name: opt.entity_name,
                            field_name: opt.field_name,
                            data_type: opt.data_type,
                          }
                        : null;
                    })()}
                    onChange={(_, newVal) => {
                      const nextKey = newVal?.value || '';
                      const nextEntity = newVal?.entity_name || '';
                      const inferredConn =
                        entityToConnection[nextEntity]?.conn_name || null;
                      const patch = { fieldKey: nextKey, operator: '' };
                      if (inferredConn && inferredConn !== cond.conn_name) {
                        patch.conn_name = inferredConn;
                      }
                      updateCondition(idx, patch);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Source Field"
                        size="small"
                        fullWidth
                        disabled={(() => {
                          if (loading || options.length === 0) return true;
                          if (connectionList.length > 1) {
                            return !cond.conn_name;
                          }
                          return false;
                        })()}
                      />
                    )}
                  />
                  <TextField
                    select
                    label="Operator"
                    value={cond.operator}
                    onChange={(e) =>
                      updateCondition(idx, { operator: e.target.value })
                    }
                    fullWidth
                    size="small"
                    SelectProps={{
                      MenuProps: DROPDOWN_MENU_PROPS,
                    }}
                    disabled={!cond.fieldKey}
                  >
                    {filteredOperatorsFor(cond.fieldKey).map((op) => (
                      <MenuItem
                        key={op.value}
                        value={op.value}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {op.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  {(() => {
                    const sel = getSelectedFieldObj(cond.fieldKey);
                    const t = (sel?.data_type || '').toLowerCase();
                    const commonSx = {};

                    if (t === 'boolean') {
                      return (
                        <TextField
                          select
                          label="Value"
                          value={cond.value}
                          onChange={(e) =>
                            updateCondition(idx, { value: e.target.value })
                          }
                          fullWidth
                          size="small"
                          sx={commonSx}
                        >
                          <MenuItem value="true">True</MenuItem>
                          <MenuItem value="false">False</MenuItem>
                        </TextField>
                      );
                    }
                    if (['int', 'double', 'currency', 'phone'].includes(t)) {
                      return (
                        <TextField
                          label="Value"
                          type="number"
                          value={cond.value}
                          onChange={(e) =>
                            updateCondition(idx, { value: e.target.value })
                          }
                          fullWidth
                          size="small"
                          sx={commonSx}
                        />
                      );
                    }
                    if (t === 'date') {
                      return (
                        <TextField
                          label="Value"
                          type="date"
                          value={cond.value}
                          onChange={(e) =>
                            updateCondition(idx, { value: e.target.value })
                          }
                          fullWidth
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          sx={commonSx}
                        />
                      );
                    }
                    if (t === 'datetime') {
                      return (
                        <TextField
                          label="Value"
                          type="datetime-local"
                          value={cond.value}
                          onChange={(e) =>
                            updateCondition(idx, { value: e.target.value })
                          }
                          fullWidth
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          sx={commonSx}
                        />
                      );
                    }
                    return (
                      <TextField
                        label="Value"
                        value={cond.value}
                        onChange={(e) =>
                          updateCondition(idx, { value: e.target.value })
                        }
                        placeholder="Enter a value"
                        fullWidth
                        size="small"
                        sx={commonSx}
                      />
                    );
                  })()}

                  {/* Actions in the same row as Source Field, Operator, Value */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      justifySelf: 'end',
                    }}
                  >
                    {conditions.length > 1 && (
                      <IconButton
                        onClick={() => removeCondition(idx)}
                        size="small"
                        aria-label="Remove condition"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton
                      onClick={addCondition}
                      size="small"
                      color="primary"
                      aria-label="Add condition"
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button onClick={handleSkip} variant="outlined" sx={{ color: '#5965A6' }} size="medium">
          Skip
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          size="medium"
          disabled={!canSubmit}
          sx={{ color: '#fff', backgroundColor: '#5965A6', '&:hover': { backgroundColor: '#4C5B9A' } }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SaveCloseDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
};

export default SaveCloseDialog;
